// The UI face, for the DOM overlay panels — not the glyph grid, which has its
// own bitmap atlas in @annwn/content. Self-hosted rather than fetched from
// Google at runtime: the client ships as a static bundle to game.pendrane.com
// and shouldn't depend on a third party being up to render its own chrome.
import '@fontsource/jersey-20/400.css';

// The component library's styles and self-hosted faces, for the migrated
// condition overlay (M6-05). Imported *before* the client's own stylesheet so
// the client `body` rule cascades last and keeps Jersey 20 for the unmigrated
// chrome — only the `.condition-overlay` surface opts into the library's data
// face. The library restyles nothing but its own `.annwn-*` classes here.
import '@annwn/ui/styles.css';

import './style.css';

import type { SelfSnapshot, SkySnapshot } from '@annwn/protocol';

import { portalLoginUrl } from './config.js';
import { Game } from './game.js';
import { fetchMe } from './net/account.js';
import { ingestTokensFromHash, loadAccessToken } from './net/identity.js';
import { mountCharacterSheet } from './ui/character-sheet-overlay.js';
import { ChatLine } from './ui/chat-line.js';
import { mountConditionOverlay } from './ui/condition-overlay.js';
import { runCreation } from './ui/creation.js';
import { mountExamineCard } from './ui/examine-card-overlay.js';
import { mountHotBar, mountStatusStrip } from './ui/hud-overlay.js';
import { mountLogOverlay } from './ui/log-overlay.js';
import { mountActionMenu } from './ui/menu-overlay.js';
import type { HudState } from './ui/hud-view-model.js';
import { mountSatchelOverlay } from './ui/satchel-overlay.js';

/**
 * Entry point. The canvas is the game; everything else on screen is a DOM
 * overlay above it (design spec §3.2 — the log is content, not chrome), which
 * is why there is no framework here and none planned.
 */
const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('missing #app root');

/**
 * The handoff from the portal (M7-02). If `/play` sent tokens in the URL
 * fragment, store them and immediately strip the fragment from the address bar,
 * so a live credential is not left in browser history or a shared screenshot.
 * Done before anything connects, so the first join already carries the token.
 */
if (ingestTokensFromHash(location.hash)) {
  history.replaceState(null, '', location.pathname + location.search);
}

/**
 * In dev the game server is a separate origin from Vite; in production both are
 * served from the same one, so a relative websocket URL is correct there and an
 * env var covers the dev case.
 */
function serverUrl(): string {
  const configured = import.meta.env['VITE_SERVER_URL'] as string | undefined;
  if (typeof configured === 'string' && configured.length > 0) return configured;

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return import.meta.env.DEV
    ? `${protocol}//${location.hostname}:2567`
    : `${protocol}//${location.host}`;
}

/**
 * A guest's name, for the local-dev fallback where there is no portal to sign
 * in at. A signed-in player is named at creation (M7-03) or by their saved
 * character; this is only ever reached by a `pnpm dev` without the web app up.
 */
function guestName(): string {
  const saved = localStorage.getItem('annwn.name');
  if (saved) return saved;

  const name = `Traveller ${Math.floor(Math.random() * 900 + 100)}`;
  localStorage.setItem('annwn.name', name);
  return name;
}

/**
 * Build the game shell and start it. Called once the boot flow has decided who
 * this is — a returning player, a freshly created character, or a guest — so
 * that the canvas is never raised for an unnamed body.
 */
function startGame(name: string, backgroundId: string | null): void {
  app!.innerHTML = `
    <canvas id="grid"></canvas>
    <div class="overlay">
      <header class="strip" id="strip"></header>
      <div class="stage">
        <aside class="condition" id="condition" aria-label="Your condition"></aside>
        <aside class="chronicle">
          <div class="log" id="log"></div>
          <form class="chat" id="chat" hidden>
            <label class="chat-label" for="chat-input">Say</label>
            <input
              id="chat-input"
              class="chat-input"
              type="text"
              autocomplete="off"
              autocapitalize="sentences"
              spellcheck="false"
            />
          </form>
        </aside>
      </div>
      <footer class="bar" id="bar"></footer>
      <div class="battle" id="battle" hidden>
        <h2 class="battle-title">The fight</h2>
        <ul class="battle-lines" id="battle-lines"></ul>
        <p class="battle-hint">v or Esc to close</p>
      </div>
      <div class="satchel" id="satchel"></div>
      <div class="sheet" id="sheet"></div>
      <div class="actions" id="actions"></div>
      <div class="talk" id="talk"></div>
      <div class="examine" id="examine"></div>
    </div>
  `;

  const canvas = document.querySelector<HTMLCanvasElement>('#grid');
  const stripPanel = document.querySelector<HTMLElement>('#strip');
  const barPanel = document.querySelector<HTMLElement>('#bar');
  const logList = document.querySelector<HTMLDivElement>('#log');
  const chatForm = document.querySelector<HTMLFormElement>('#chat');
  const chatField = document.querySelector<HTMLInputElement>('#chat-input');
  const conditionPanel = document.querySelector<HTMLElement>('#condition');
  const battlePanel = document.querySelector<HTMLDivElement>('#battle');
  const battleLines = document.querySelector<HTMLUListElement>('#battle-lines');
  const satchelPanel = document.querySelector<HTMLDivElement>('#satchel');
  const sheetPanel = document.querySelector<HTMLDivElement>('#sheet');
  const actionsPanel = document.querySelector<HTMLDivElement>('#actions');
  const talkPanel = document.querySelector<HTMLDivElement>('#talk');
  const examinePanel = document.querySelector<HTMLDivElement>('#examine');
  if (
    !canvas ||
    !stripPanel ||
    !barPanel ||
    !logList ||
    !chatForm ||
    !chatField ||
    !conditionPanel ||
    !battlePanel ||
    !battleLines ||
    !satchelPanel ||
    !sheetPanel ||
    !actionsPanel ||
    !talkPanel ||
    !examinePanel
  ) {
    throw new Error('client shell failed to build');
  }

  const log = mountLogOverlay(logList, {
    onHighlight: (actors) => {
      game.setHighlight(actors);
    },
  });

  /**
   * The status strip (RC2-07) — the top of the HUD, and the last hand-rolled
   * surface to go. `Meter`s for body and breath, `StatusTag`s for what is true
   * this second, and the sky's clock at the far end.
   *
   * The sky lives *in* the strip now rather than on a line of its own. M3-05
   * kept them apart on the argument that the strip is about you and the weather
   * is about the valley; the approved reference puts them on one rule and it is
   * right, because they are read in the same glance and the split cost the
   * screen a whole band of chrome to say so.
   *
   * It takes the connection status too, which is the one non-diegetic thing on
   * screen: it shows while the socket is down and is empty the rest of the time,
   * so a healthy game has no debug text on it at all.
   */
  const strip = mountStatusStrip(stripPanel);

  /** The last of each, so either callback can redraw the whole strip. */
  let lastSelf: SelfSnapshot | null = null;
  let lastSky: SkySnapshot | null = null;
  let lastStatus = 'Waking…';
  const drawStrip = (): void => {
    strip.update(lastSelf, lastSky, lastStatus);
  };
  drawStrip();

  /**
   * The condition overlay — the body figure, wound ledger and status tags, now a
   * React island rendered from `@annwn/ui` (M6-05). Clicking a part is both "tell
   * me about this" and "aim here": one gesture for one intention, because a
   * corner panel has no room for a mode. The island receives view models only —
   * the numbers never leave the adapter.
   */
  const condition = mountConditionOverlay(conditionPanel, {
    onAim: (partId) => {
      game.aimAt(partId);
    },
  });

  /**
   * The unabridged fight (M2-06). Rendered on open rather than kept in sync: it
   * is a thing you stop to read, and a panel re-laying itself out while you read
   * it would be the same rudeness as the log stealing your scroll position.
   */
  const drawBattle = (open: boolean): void => {
    battlePanel.hidden = !open;
    if (!open) return;

    const document_ = battlePanel.ownerDocument;
    const fragment = document_.createDocumentFragment();

    for (const line of log.battle()) {
      const item = document_.createElement('li');
      item.className = `log-line log-${line.kind}`;
      item.textContent = `${line.stamp}  ${line.text}`;
      fragment.append(item);
    }

    if (!fragment.hasChildNodes()) {
      const empty = document_.createElement('li');
      empty.className = 'log-line log-system';
      empty.textContent = 'No fight yet.';
      fragment.append(empty);
    }

    battleLines.replaceChildren(fragment);
    battleLines.scrollTop = battleLines.scrollHeight;
  };

  /**
   * The satchel (M3-03) — now a React island rendered from `@annwn/ui` (RC1-05):
   * a framed window over a table of what you carry, with a popup describing any
   * row you open. It receives view models only; `satchelView` still turns the
   * snapshot into rows, and the numbers a bag is allowed never leave it.
   */
  const satchel = mountSatchelOverlay(satchelPanel, {
    onClose: () => {
      game.closeSatchel();
    },
    onDrop: (stackKey) => {
      game.dropItem(stackKey);
    },
    onEquip: (stackKey) => {
      game.equipItem(stackKey);
    },
    onStow: () => {
      game.stowWeapon();
    },
  });

  /**
   * The character sheet (RC2-03) — the milestone's flagship `@annwn/ui` surface:
   * a full-screen, tabbed island reading identity, attributes, the familiarity
   * ladder, the paper-doll and a categorised inventory off the same `self`
   * snapshot everything else does. Its once-only "getting better" glow remembers
   * itself in `localStorage`, so a rung glows the viewing it is crossed in and
   * never again.
   */
  const sheet = mountCharacterSheet(sheetPanel, {
    onClose: () => {
      game.closeSheet();
    },
    onEquip: (stackKey) => {
      game.equipItem(stackKey);
    },
    onStow: () => {
      game.stowWeapon();
    },
    onCraft: (verb) => {
      game.closeSheet();
      game.craft(verb);
    },
    store: window.localStorage,
  });

  /**
   * The action menu — a focused `@annwn/ui` `Popup` over a `Menu` (RC1-05), the
   * openable, keyboard-owning surface M6-05 named. `Game` builds the verb list and
   * suspends its own input while it is open; the island reports the chosen verb or
   * the cancel, and `Game.chooseAction` resolves and sends it.
   */
  const actions = mountActionMenu(actionsPanel, {
    onChoose: (id) => {
      game.chooseAction(id);
    },
    onCancel: () => {
      game.chooseAction(null);
    },
  });

  /** NPC conversations are focused popups with clickable response rows. */
  const talk = mountActionMenu(talkPanel, {
    onChoose: (id) => {
      game.chooseTalk(id);
    },
    onCancel: () => {
      game.chooseTalk(null);
    },
  });

  /**
   * The look card (RC2-07) — a `Popup` naming what the cursor is over, its
   * quality at a glance, and the verbs that apply to it. The examine answer used
   * to be one more line scrolling past in the chronicle; a question deserves an
   * answer that stays still while you read it.
   */
  const examine = mountExamineCard(examinePanel, {
    onVerb: (id) => {
      game.examineVerb(id);
    },
    onClose: () => {
      game.closeExamine();
    },
  });

  /**
   * The action hotbar (RC2-07) — the bottom row, composed from `Button` and
   * `Badge` rather than promoted to a library `HotBar` (the ticket's owner
   * decision; nothing else in the product wants one yet).
   *
   * It carries the *real* bindings rather than the reference's `1`–`6`, so it is
   * also the key legend: it is why the client no longer greets a new player with
   * a paragraph of instructions in the log.
   */
  const bar = mountHotBar(barPanel, {
    onSlot: (id) => {
      game.hotbarAction(id);
    },
  });

  /**
   * What only `Game` knows — which overlays are open, whether the look cursor is
   * out, who is within reach. Held here so a snapshot redraw and a state change
   * both draw the same bar.
   */
  let hud: HudState = {};

  const game = new Game({
    canvas,
    serverUrl: serverUrl(),
    name,
    backgroundId,
    onLog: (events) => {
      log.append(events);
    },
    onNote: (text) => {
      log.note(text, game.currentTick);
    },
    onChat: (open) => {
      if (open) chat.show();
      else chat.close();
    },
    onStatus: (text) => {
      lastStatus = text;
      drawStrip();
    },
    onIdentity: (actorId) => {
      log.setViewer(actorId);
    },
    onSelf: (self, tick) => {
      // The strip and the bar read the same snapshot the condition panel does,
      // so nothing on the HUD can drift from anything else on it.
      lastSelf = self;
      drawStrip();
      bar.update(self, hud);
      // One update for the whole condition island: the React overlay derives the
      // body figure, wound ledger, status tags and prose line from the same
      // snapshot, so none of them can drift from the others or from the server.
      condition.update(self?.combat ?? null, tick);
      // The satchel island redraws from the same snapshot the condition does, so
      // what it shows cannot drift from what the server last said (M3-03).
      satchel.update(
        self?.satchel ?? null,
        self?.combat?.weapon?.name ?? null,
        self?.combat?.weapon?.id ?? null,
        self?.combat?.weapon?.stowable ?? false,
      );
      // The character sheet reads the whole self — its paper-doll, inventory,
      // attributes and familiarity ladder all come off this one snapshot (RC2-03).
      sheet.update(self ?? null, tick);
    },
    onSky: (state) => {
      lastSky = state;
      drawStrip();
    },
    onBattle: drawBattle,
    onSatchel: (open) => {
      satchel.setOpen(open);
    },
    onSheet: (open) => {
      sheet.setOpen(open);
    },
    onActions: (request) => {
      if (request) actions.open(request);
      else actions.close();
    },
    onTalk: (request) => {
      if (request) talk.open(request);
      else talk.close();
    },
    onExamine: (card) => {
      examine.show(card);
    },
    onHud: (state) => {
      hud = state;
      bar.update(lastSelf, state);
    },
  });

  // Built after the game because it hands lines straight to it, and told about
  // its own open/close so the game knows when the keyboard has left.
  const chat = new ChatLine(chatForm, chatField, {
    onSubmit: (text) => {
      game.say(text);
    },
    onToggle: (open) => {
      game.setChatOpen(open);
    },
  });

  // One line, not eleven. RC2-07's hotbar is the key legend now — every verb it
  // carries is on the bar with the letter that runs it — so the log opens with
  // the two things the bar cannot show: how to walk, and how to speak.
  log.note('Arrow keys, HJKL or the numpad to walk. Click or right-click a tile to inspect it.');

  game.start().catch((error: unknown) => {
    // A refused protocol version arrives here. It is the one error a player can
    // actually do something about, so it goes on the screen rather than into the
    // console.
    const message = error instanceof Error ? error.message : String(error);
    lastStatus = message;
    drawStrip();
    console.error('[client]', error);
  });
}

/**
 * Decide who is arriving, then start the game (M7-03).
 *
 * The valley is behind a sign-in now. A browser with no token is sent to the
 * portal to log in or claim an invite; a signed-in browser asks the API whether
 * this account has a character yet, and either shows creation or joins straight
 * onto the one it has. The one door left open is a local `pnpm dev` with no
 * portal configured, which still drops a guest into the world so the game can be
 * run without standing the whole stack up.
 */
async function boot(): Promise<void> {
  const token = loadAccessToken();

  if (!token) {
    const portal = portalLoginUrl();
    if (portal) {
      location.assign(portal);
      return;
    }
    startGame(guestName(), null);
    return;
  }

  const me = await fetchMe(token);
  if (me === null) {
    // The token is expired or the account is gone: back to the portal to sign in
    // again, or — with no portal in dev — a guest session rather than a dead end.
    const portal = portalLoginUrl();
    if (portal) {
      location.assign(portal);
      return;
    }
    startGame(guestName(), null);
    return;
  }

  if (!me.hasCharacter) {
    const choice = await runCreation(app!);
    startGame(choice.name, choice.backgroundId);
    return;
  }

  startGame(me.name ?? guestName(), null);
}

void boot();
